import { Modal, Spin, Tag, Typography, Space, Alert, Tooltip, message } from 'antd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { getAgentsForSkill, removeSkill, type AgentLinkRow } from '@/api/skills';
import { colors } from '@/theme/tokens';
import type { AiSkill } from '@/api/types';

const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// DeleteSkillModal — opens an antd Modal.confirm flow that previews linked
// agents and asks the editor to confirm an unlink-and-delete in plain English.
//
// Why a function instead of a JSX component:
//   The list page needs to async-fetch the linked-agent set BEFORE deciding
//   what to ask. Driving that imperatively with Modal.confirm + Modal.update
//   keeps the SkillsListPage clean of extra UI state. Same pattern Ant uses
//   for "are you sure?" confirms throughout the app.
//
// UX intent (per "non-tech-savvy users" guidance):
//   - Plain-English copy. No "FK", no "cascade", no "orphan".
//   - When agents are linked, the modal shows them as friendly chips.
//   - The danger button reads "Unlink and delete" — verb-first, action-clear.
//   - When nothing is linked, a simpler one-line confirm.
//   - Error messages tell the user what to do next.
// ─────────────────────────────────────────────────────────────────────────────

export function openDeleteSkillModal(skill: AiSkill, onDeleted: () => void): void {
  // Open with a "loading" body first so the click feels instant even while
  // we fetch the linked-agent list. Modal.update lets us swap the body in
  // place once the fetch resolves.
  const modal = Modal.confirm({
    title: 'Delete this skill?',
    icon: <DeleteOutlined style={{ color: colors.red[500] }} />,
    width: 540,
    okText: 'Loading…',
    okButtonProps: { disabled: true, danger: true },
    cancelText: 'Cancel',
    content: (
      <div style={{ paddingTop: 8 }}>
        <Spin size="small" /> <Text type="secondary"> Checking which agents use this skill…</Text>
      </div>
    ),
    onOk: () => Promise.resolve(),
  });

  void getAgentsForSkill(skill.id)
    .then((agents) => {
      modal.update(buildModalConfig(skill, agents, onDeleted, modal));
    })
    .catch((err) => {
      modal.update({
        okText: 'Close',
        okButtonProps: { danger: false },
        cancelButtonProps: { style: { display: 'none' } },
        content: (
          <Alert
            type="error"
            showIcon
            message="Couldn't check which agents use this skill"
            description={
              <span>
                {err instanceof Error ? err.message : String(err)} <br />
                Please refresh and try again.
              </span>
            }
          />
        ),
        onOk: () => Promise.resolve(),
      });
    });
}

function buildModalConfig(
  skill: AiSkill,
  agents: AgentLinkRow[],
  onDeleted: () => void,
  // `modal` reserved for future Modal.update calls (e.g. "unlinking…" in-flight
  // state). Underscore-prefixed so strict tsc doesn't flag it as unused while
  // the parameter shape stays explicit at the call site.
  _modal: ReturnType<typeof Modal.confirm>,
) {
  const linkedCount = agents.length;
  const isLinked = linkedCount > 0;

  // Common doer — wraps removeSkill with friendly toasts + parent refresh.
  // Re-used by both the simple "delete" and the "unlink + delete" paths.
  const doDelete = async (unlink: boolean): Promise<void> => {
    try {
      const outcome = await removeSkill(skill.id, { unlinkFromAgents: unlink });
      if (outcome.status === 'Removed') {
        if (outcome.linksRemoved > 0) {
          void message.success(
            `Deleted "${skill.name}". Unlinked from ${outcome.linksRemoved} agent${outcome.linksRemoved === 1 ? '' : 's'}.`,
          );
        } else {
          void message.success(`Deleted "${skill.name}".`);
        }
        onDeleted();
      } else if (outcome.status === 'RefusedHasLinks') {
        // Race condition guard — another tab linked an agent between our
        // preview load and the click. Re-prompt with the fresh count.
        void message.warning(
          `Another change linked this skill to ${outcome.linkedAgentIds.length} agent${outcome.linkedAgentIds.length === 1 ? '' : 's'}. Please retry.`,
        );
      } else {
        void message.error('Skill not found — it may have already been deleted.');
        onDeleted();
      }
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Failed to delete the skill.');
      throw err; // Modal.confirm keeps the dialog open on rejected promise.
    }
  };

  if (!isLinked) {
    // ── Simple path: nothing to unlink ──
    return {
      okText: 'Delete skill',
      okButtonProps: { danger: true },
      content: (
        <div style={{ paddingTop: 8 }}>
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>{skill.name}</Text> isn&apos;t currently in use by any agent.
          </Paragraph>
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            Deleting it removes the skill definition. This cannot be undone.
          </Text>
        </div>
      ),
      onOk: () => doDelete(/* unlink */ false),
    };
  }

  // ── Linked path: show the agent list + ask for explicit unlink-and-delete ──
  return {
    okText: `Unlink and delete (${linkedCount})`,
    okButtonProps: { danger: true, icon: <WarningOutlined /> },
    content: (
      <div style={{ paddingTop: 8 }}>
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message={
            <span>
              <Text strong>{skill.name}</Text> is currently used by {linkedCount} agent
              {linkedCount === 1 ? '' : 's'}.
            </span>
          }
          description={
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              If you delete it, those agents will stop using this skill the next time they
              start a chat. The skill itself can&apos;t be recovered after deletion.
            </Text>
          }
          style={{ marginBottom: 12 }}
        />

        <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
          Will be unlinked from:
        </Text>
        <Space size={[6, 6]} wrap>
          {agents.map((a) => (
            <Tooltip
              key={a.id}
              title={a.canonicalName ? `Internal id: ${a.canonicalName}` : `Agent id ${a.id}`}
            >
              <Tag icon={<RobotOutlined />} color="blue" style={{ borderRadius: 999 }}>
                {a.displayName ?? `Agent ${a.id}`}
              </Tag>
            </Tooltip>
          ))}
        </Space>

        <div
          style={{
            marginTop: 14,
            padding: '8px 12px',
            borderRadius: 8,
            background: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
            color: colors.slate[600],
            fontSize: 12.5,
            lineHeight: 1.55,
          }}
        >
          We&apos;ll unlink the skill from these agents and delete it in one step.
        </div>
      </div>
    ),
    onOk: () => doDelete(/* unlink */ true),
  };
}
